import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { doc, onSnapshot } from 'firebase/firestore';

import CashfreePaymentBridge from '../../components/CashfreePaymentBridge';
import { db } from '../../firebase';
import Loading from '../../assets/loading.gif';
import PageContainer from '../layouts/PageContainer';
import { buildPilotV2CashfreeOrderDetails, roundCashfreeOrderAmount } from '../services/cashfreeOrderDetails';
import { V2_PAYMENT_TYPE } from '../services/checkout';
import { clearCart } from '../services/commerce';
import { pilotCashfreeOrderPayments, pilotCreateCashfreeSession } from '../services/payments';
import { useV2Session } from '../state/SessionProvider';

const LOG = '[V2Pay]';

/** Wait until after React commits paint (drop target must exist in layout). */
function waitNextPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}

function summaryNavigateState(checkoutSummaryState, extra = {}) {
  return {
    deliveryDate: checkoutSummaryState?.deliveryDate,
    timeSlot: checkoutSummaryState?.timeSlot,
    paymentType: checkoutSummaryState?.paymentType ?? V2_PAYMENT_TYPE.ONLINE,
    instructions: checkoutSummaryState?.instructions,
    ...extra,
  };
}

/**
 * @param {{ pilotOrderPayload: object, receipt: object|null|undefined, checkoutSummaryState: object, mobile: string }} props
 */
function CheckoutPayInner({ pilotOrderPayload, receipt, checkoutSummaryState, mobile }) {
  const navigate = useNavigate();

  const [phase, setPhase] = useState('init');
  const [checkPayment, setCheckPayment] = useState(false);
  const unsubTxn = useRef(null);
  const failureNavigated = useRef(false);
  const successDone = useRef(false);

  const goToSummaryFailure = useCallback(
    (message, logDetail = {}) => {
      if (failureNavigated.current) return;
      failureNavigated.current = true;
      console.warn(LOG, 'failure_navigate_summary', { message, ...logDetail });
      if (unsubTxn.current) {
        unsubTxn.current();
        unsubTxn.current = null;
      }
      navigate('/v2/checkout/summary', {
        replace: true,
        state: summaryNavigateState(checkoutSummaryState, {
          paymentFailed: true,
          paymentFailureMessage: message || 'Your payment could not be completed.',
        }),
      });
    },
    [navigate, checkoutSummaryState]
  );

  useEffect(() => {
    console.info(LOG, 'effect_start', {
      platform: Capacitor.getPlatform(),
      hasMobile: Boolean(mobile),
      total: pilotOrderPayload?.total,
      productCount: Array.isArray(pilotOrderPayload?.products) ? pilotOrderPayload.products.length : 0,
    });

    if (!mobile) {
      goToSummaryFailure('Please sign in to pay.', { reason: 'no_mobile' });
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const orderDetails = buildPilotV2CashfreeOrderDetails({
          userId: String(mobile).trim(),
          pilotOrderPayload,
        });

        const amount = roundCashfreeOrderAmount(pilotOrderPayload.total);
        const cashfreeBody = {
          amount,
          mobileNo: String(mobile).trim(),
          customerId: String(mobile).trim(),
          platform: Capacitor.getPlatform(),
          orderDetails,
          version: process.env.REACT_APP_VERSION,
        };

        console.info(LOG, 'create_session_start', { amount, platform: cashfreeBody.platform });
        const data = await pilotCreateCashfreeSession(cashfreeBody);
        if (cancelled) return;

        console.info(LOG, 'create_session_ok', {
          order_id: data.order_id,
          session_prefix: data.payment_session_id
            ? `${String(data.payment_session_id).slice(0, 12)}…`
            : null,
        });

        unsubTxn.current = onSnapshot(doc(db, 'transactions', data.order_id), (snap) => {
          const txnData = snap.data();
          const st = txnData?.status;
          console.info(LOG, 'firestore_txn_snapshot', { order_id: data.order_id, status: st ?? 'no_status' });

          if (st === 'PAYMENT_SUCCESS') {
            if (successDone.current) return;
            const pilotOrderId = txnData.pilotOrderId != null ? String(txnData.pilotOrderId).trim() : '';
            if (!pilotOrderId) {
              goToSummaryFailure(
                'Payment succeeded but we could not confirm your order. Please contact support with your payment receipt.',
                { reason: 'success_missing_pilotOrderId' }
              );
              return;
            }
            successDone.current = true;
            if (unsubTxn.current) {
              unsubTxn.current();
              unsubTxn.current = null;
            }
            console.info(LOG, 'navigate_order_status', { pilotOrderId });
            (async () => {
              try {
                await clearCart();
              } catch (e) {
                console.warn(LOG, 'clearCart_warn', e);
              }
              if (!cancelled) {
                navigate('/v2/order-status', {
                  replace: true,
                  state: { orderId: pilotOrderId, receipt: receipt || null },
                });
              }
            })();
          } else if (st === 'PAYMENT_FAILED') {
            goToSummaryFailure('Your payment was declined or cancelled.', { reason: 'firestore_PAYMENT_FAILED' });
          }
        });

        setPhase('paying');

        const dropConfig = {
          components: ['order-details', 'upi', 'card', 'app', 'netbanking', 'paylater'],
          onSuccess: () => {
            console.info(LOG, 'cashfree_drop_onSuccess');
            setCheckPayment(true);
            setPhase('confirming');
          },
          onFailure: () => {
            goToSummaryFailure('Payment was not completed.', { reason: 'cashfree_drop_onFailure' });
          },
          style: {
            backgroundColor: '#ffffff',
            color: '#a4243d',
            fontFamily: 'Lato',
            fontSize: '14px',
            errorColor: '#ff0000',
            theme: 'light',
          },
        };

        if (Capacitor.getPlatform() === 'web') {
          await waitNextPaint();
          let el = document.getElementById('v2-payment-form');
          let attempts = 0;
          while (!el && attempts < 25) {
            console.info(LOG, 'wait_payment_form_mount', { attempt: attempts + 1 });
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 40));
            el = document.getElementById('v2-payment-form');
            attempts += 1;
          }
          if (!el) {
            goToSummaryFailure('Payment form could not be loaded. Please try again.', {
              reason: 'missing_v2_payment_form_dom',
            });
            return;
          }

          if (typeof window.Cashfree !== 'function') {
            console.error(LOG, 'window.Cashfree_missing', { typeofCashfree: typeof window.Cashfree });
            goToSummaryFailure(
              'Payment checkout could not load (Cashfree script). Refresh the page or try the app again.',
              { reason: 'cashfree_global_missing' }
            );
            return;
          }

          try {
            console.info(LOG, 'cashfree_drop_start');
            const cashfree = new window.Cashfree(data.payment_session_id);
            cashfree.drop(el, dropConfig);
          } catch (err) {
            console.error(LOG, 'cashfree_drop_throw', err);
            goToSummaryFailure(err instanceof Error ? err.message : 'Could not open payment checkout.', {
              reason: 'cashfree_drop_throw',
            });
          }
        } else {
          console.info(LOG, 'native_bridge_start');
          const resp = await CashfreePaymentBridge.initiatePayment({
            paymentSessionId: data.payment_session_id,
            orderId: data.order_id,
          });
          console.info(LOG, 'native_bridge_result', { status: resp?.status });
          if (resp.status === 'SUCCESS') {
            await pilotCashfreeOrderPayments(data.order_id);
            setCheckPayment(true);
            setPhase('confirming');
          } else {
            goToSummaryFailure('Could not open the payment app.', { reason: 'native_bridge_not_success' });
          }
        }
      } catch (e) {
        console.error(LOG, 'checkout_pay_catch', e);
        const msg = e instanceof Error ? e.message : 'Could not start payment.';
        goToSummaryFailure(msg, { reason: 'async_catch' });
      }
    })();

    return () => {
      cancelled = true;
      console.info(LOG, 'effect_cleanup');
      if (unsubTxn.current) {
        unsubTxn.current();
        unsubTxn.current = null;
      }
    };
  }, [mobile, pilotOrderPayload, receipt, navigate, goToSummaryFailure]);

  return (
    <PageContainer>
      <Box
        sx={{
          minHeight: '50vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {phase === 'init' ? (
          <>
            <CircularProgress size={40} />
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Preparing secure checkout…
            </Typography>
          </>
        ) : null}

        {phase === 'confirming' || (checkPayment && phase !== 'init') ? (
          <Box sx={{ textAlign: 'center', px: 2 }}>
            <Box
              component="img"
              src={Loading}
              alt=""
              sx={{ width: 120, height: 'auto', mx: 'auto', display: 'block' }}
            />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Checking payment status… Please wait. Do not close this screen.
            </Typography>
          </Box>
        ) : null}

        {/*
          Must stay mounted before Cashfree.drop runs — if this only rendered after setPhase('paying'),
          getElementById raced React paint and returned null (immediate failure after ~session latency).
        */}
        <Box
          id="v2-payment-form"
          sx={{
            width: '100%',
            maxWidth: 480,
            minHeight: 120,
            visibility: phase === 'paying' && !checkPayment && Capacitor.getPlatform() === 'web' ? 'visible' : 'hidden',
            position: 'relative',
          }}
        />
      </Box>
    </PageContainer>
  );
}

export default function CheckoutPay() {
  const location = useLocation();
  const { mobile } = useV2Session();
  const pilotOrderPayload = location.state?.pilotOrderPayload;
  const receipt = location.state?.receipt;
  const checkoutSummaryState = location.state?.checkoutSummaryState;

  if (!pilotOrderPayload || !checkoutSummaryState?.deliveryDate || !checkoutSummaryState?.timeSlot) {
    console.warn(LOG, 'redirect_missing_nav_state', {
      hasPayload: Boolean(pilotOrderPayload),
      hasDeliveryDate: Boolean(checkoutSummaryState?.deliveryDate),
      hasTimeSlot: Boolean(checkoutSummaryState?.timeSlot),
    });
    return (
      <Navigate
        to="/v2/checkout/summary"
        replace
        state={checkoutSummaryState && typeof checkoutSummaryState === 'object' ? { ...checkoutSummaryState } : {}}
      />
    );
  }

  return (
    <CheckoutPayInner
      pilotOrderPayload={pilotOrderPayload}
      receipt={receipt}
      checkoutSummaryState={checkoutSummaryState}
      mobile={mobile}
    />
  );
}
